<template>
  <q-input
    v-bind="mergedAttrs"
    :value="lazyInput || dateFr"
    :mask="mask.input"
    fill-mask="_"
    @input="onTyping"
    @clear="$emit('input', null)"
  >
    <template
      v-if="nav && !readonly"
      #before
    >
      <q-btn
        flat
        round
        icon="fa fa-chevron-left"
        @click.capture.stop="offset(-1)"
      />
    </template>
    <template
      v-if="hasDate && !readonly"
      #prepend
    >
      <q-icon
        name="fa fa-calendar-alt"
        class="cursor-pointer"
      >
        <q-popup-proxy
          ref="datePicker"
          transition-show="scale"
          transition-hide="scale"
        >
          <q-date
            :value="value || lazyTime"
            :mask="modelMask.iso"
            today-btn
            @input="onInput"
          />
        </q-popup-proxy>
      </q-icon>
    </template>
    <template
      v-if="hasTime && !readonly"
      #append
    >
      <q-icon
        name="fa fa-clock"
        class="cursor-pointer"
      >
        <q-popup-proxy
          ref="timePicker"
          transition-show="scale"
          transition-hide="scale"
        >
          <q-time
            :value="value || lazyDate"
            :mask="modelMask.iso"
            format24h
            now-btn
            @input="onInput"
          />
        </q-popup-proxy>
      </q-icon>
    </template>
    <template
      v-if="nav"
      #after
    >
      <q-btn
        flat
        round
        icon="fa fa-chevron-right"
        @click.stop="offset(1)"
      />
    </template>
  </q-input>
</template>

<script>
import { date } from 'quasar'
import { DATE_FR, DATE_ISO, DATETIME_FR, DATETIME_ISO, TIME } from '@/consts/date'
import { dateFormatIso, parseIso, timeFormat } from '@/utils/data/date'
import propsReadonly from '@/mixins/propsReadonly'
import validateProxyMixin from '@/mixins/validateProxyMixin'

const masks = {
  date: {
    iso: DATE_ISO,
    fr: DATE_FR,
    input: '##/##/####',
    regexp: /^([0-2][0-9]|(3)[0-1])(\/)(((0)[0-9])|((1)[0-2]))(\/)\d{4}$/
  },
  time: {
    iso: TIME,
    fr: TIME,
    input: '##:##',
    regexp: /^([01]\d|2[0-3]):([0-5]\d)$/
  },
  datetime: {
    iso: DATETIME_ISO,
    fr: DATETIME_FR,
    input: '##/##/#### ##:##',
    regexp: /^([0-2][0-9]|(3)[0-1])(\/)(((0)[0-9])|((1)[0-2]))(\/)\d{4} ([01]\d|2[0-3]):([0-5]\d)$/
  }
}

/**
 * Datepicker générique pour LWF
 */
export default {
  name: 'LwfDatePicker',
  mixins: [propsReadonly, validateProxyMixin],
  inheritAttrs: false,
  props: {
    /**
     * @model
     */
    value: {
      type: String,
      default: undefined
    },
    /**
     * Le type de picker
     */
    type: {
      type: String,
      default: 'datetime'
    },
    /**
     * Permet d'ajouter la fonction navigation dans les dates avec des flèches
     */
    nav: {
      type: Boolean
    },

    /**
     * Permet de choisir le format de sortie
     */
    emitType: {
      type: String,
      default: undefined
    },

    /**
     * Permet de n'emit le vModel que lorsqu'une date et heure complète est sélectionnée
     */
    lazy: Boolean,

    rules: {
      type: Array,
      default: _.stubArray
    }
  },
  data () {
    return {
      lazyDate: null,
      lazyTime: null,
      lazyInput: null
    }
  },
  computed: {
    vRules () {
      return [
        this.$validators.optional(this.validateDate),
        ...this.rules
      ].map((r) => () => r(this.value)) // On compare avec la date format ISO
    },
    mask () {
      return masks[this.type]
    },
    modelMask () {
      return this.emitType ? masks[this.emitType] : this.mask
    },
    hasTime () {
      return this.type.indexOf('time') !== -1
    },
    hasDate () {
      return this.type.indexOf('date') !== -1
    },
    dateFr () {
      return this.value
        ? this.type === 'time' && ['datetime', 'date'].indexOf(this.emitType) === -1
          ? this.value
          : date.formatDate(this.value, this.mask.fr)
        : ''
    },
    mergedAttrs () {
      const { $attrs, vRules, readonly } = this
      return {
        clearable: !readonly,
        readonly,
        ...$attrs,
        rules: vRules
      }
    }
  },
  watch: {
    value: {
      immediate: true,
      handler (v) {
        const { type } = this
        const now = new Date()
        this.lazyDate = type === 'time' ? dateFormatIso(now) : v
        this.lazyTime = type === 'date' ? timeFormat(now) : v
      }
    }
  },
  methods: {
    offset (i) {
      this.$emit('input', date.formatDate(date.addToDate(parseIso(this.value), { days: i }), this.mask.iso))
    },
    onTyping (v) {
      const { modelMask: { regexp, iso, fr } } = this
      this.lazyInput = v
      this.$emit(
        'input',
        regexp.test(v)
          ? date.formatDate(date.extractDate(v, fr), iso)
          : null
      )
      this.$nextTick(this.validate)
    },
    /**
     * Hanler de la saisie utilisateur
     * Bascule en timepicker après le datepicker
     * Ferme automatiquement le timepicker
     * @param v
     * @param type
     */
    onInput (v, type) {
      this.lazyInput = null
      if (['add-day', 'today'].indexOf(type) !== -1) {
        this.lazyDate = v
        this.$refs.datePicker.hide()
        if (this.hasTime) {
          this.$refs.timePicker.show()
        }
      } else {
        this.lazyTime = v
        this.$refs.timePicker.hide()
      }
      /**
       * @event input
       * @property {String}
       */
      if (!this.lazy || (this.lazyTime && this.lazyTime)) {
        this.$emit('input', v)
      }
    },
    validateDate () {
      if (this.lazyInput) {
        if (!this.value) {
          return 'Format français invalide'
        }
        if (this.type !== 'time') {
          const [dateFr] = this.lazyInput.split('/')
          const [, , dateIso] = this.lazyDate.split('-')
          const [dateTIso] = (dateIso || '').split('T') // Date du dateTime
          if (dateFr !== dateTIso) {
            return `La date n'existe pas`
          }
        }
      }
    }
  }
}
</script>

<style scoped>

</style>
